import { capitalizeExportValue } from 'lib/helpers/capitalizeExportValue';

import { generateHeaderName } from 'components/practice/BeyondBilling/Reports/components/ReportView/lib/generateColumnDefs';

import exportToCsv from './exportToCsv';
import exportToExcel from './exportToExcel';
import { flattenNestedData } from './flattenNestedData';
import { EMPTY_CELL, buildCsvTotalsRow, describeMeasures } from './spreadsheetTotals';

const namespaceParentColumns = (parentColumns, childColumns) => {
  const childHeaderNames = new Set(childColumns.map((col) => col.headerName));

  return parentColumns.map((col) => ({
    ...col,
    field: `parent_${col.field}`,
    headerName: childHeaderNames.has(col.headerName) ? `Parent ${col.headerName}` : col.headerName
  }));
};

const buildUniqueHeaders = (columns) => {
  const usedLabels = new Set();

  return columns.map((col) => {
    const baseLabel = col.headerName || generateHeaderName(String(col.field));

    let label = baseLabel;
    let suffix = 2;
    while (usedLabels.has(label)) {
      label = `${baseLabel} (${suffix})`;
      suffix += 1;
    }
    usedLabels.add(label);

    return { field: col.field, label };
  });
};

export const exportSpreadsheet = ({
  values,
  data,
  finalColumns,
  finalParentColumns,
  isDetailedMode,
  hasExpandableRows,
  // Reports only: their columns follow generateColumnDefs, so amounts and counts can be typed
  // and totalled. Other tables' numeric columns may be unit prices, ids or per-row rates.
  withTotals = false,
  statusPanelData
}) => {
  const flattenedData = flattenNestedData(data, finalColumns, {
    mode: hasExpandableRows ? values.export_data : 'summary',
    parentColumns: isDetailedMode ? finalParentColumns : [],
    omitRepeatedMeasures: withTotals
  });

  const headerColumns = isDetailedMode
    ? [...namespaceParentColumns(finalParentColumns, finalColumns), ...finalColumns]
    : finalColumns;

  const headers = buildUniqueHeaders(headerColumns);

  const formattedDataColumns = flattenedData.map((row) =>
    Object.fromEntries(
      headers.map(({ field, label }) => {
        const value = row[field];
        return [
          label,
          value === undefined || value === null || value === ''
            ? EMPTY_CELL
            : capitalizeExportValue(value)
        ];
      })
    )
  );

  const labels = headers.map(({ label }) => label);
  const measures = withTotals
    ? describeMeasures({
        data,
        headers,
        parentColumns: finalParentColumns,
        isDetailedMode,
        statusPanelData
      })
    : new Map();

  if (values.export_type.includes('csv')) {
    const totalsRow = buildCsvTotalsRow(formattedDataColumns, labels, measures);

    exportToCsv(
      totalsRow ? [...formattedDataColumns, totalsRow] : formattedDataColumns,
      values.title
    );
  }

  if (values.export_type.includes('excel')) {
    exportToExcel(formattedDataColumns, values.title, { measures });
  }
};
